import React, { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { ArrowLeft, PackageOpen, Loader2 } from 'lucide-react';
import { Badge } from '../components/ui/badge';
import ProductCard from '../components/ProductCard';
import api, { absoluteAsset } from '../services/api';

const BrandDetail = () => {
  const { id } = useParams();
  const [brand, setBrand] = useState(null);
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    let on = true;
    setLoading(true);
    api.get(`/brands/${id}`)
      .then(async ({ data }) => {
        if (!on) return;
        setBrand({ ...data, logo: absoluteAsset(data.logo) });
        const allProds = await api.get('/products');
        if (!on) return;
        setProducts(
          allProds.data
            .filter(p => p.brandId === id)
            .map(p => ({ ...p, image: absoluteAsset(p.image) }))
        );
      })
      .catch(() => on && setNotFound(true))
      .finally(() => on && setLoading(false));
    return () => { on = false; };
  }, [id]);

  if (loading) {
    return <div className="flex justify-center py-24"><Loader2 className="h-8 w-8 animate-spin text-emerald-700" /></div>;
  }

  if (notFound || !brand) {
    return (
      <div className="mx-auto max-w-3xl px-4 py-20 text-center sm:px-6">
        <h2 className="text-2xl font-semibold text-slate-800">Marca no encontrada</h2>
        <Link to="/" className="mt-4 inline-flex items-center gap-2 text-emerald-700 hover:text-emerald-800">
          <ArrowLeft className="h-4 w-4" /> Volver a Marcas
        </Link>
      </div>
    );
  }

  return (
    <div className="mx-auto max-w-7xl px-4 py-10 sm:px-6 lg:px-8">
      <Link to="/" className="inline-flex items-center gap-2 text-sm font-medium text-emerald-700 transition-colors hover:text-emerald-800">
        <ArrowLeft className="h-4 w-4" />
        Volver a Marcas
      </Link>

      <div className="mt-6 flex flex-col items-start gap-6 rounded-2xl border border-slate-200 bg-white p-8 shadow-sm sm:flex-row sm:items-center">
        <div className="flex h-44 w-64 shrink-0 items-center justify-center overflow-hidden rounded-xl bg-slate-50 p-4">
          {brand.logo && <img src={brand.logo} alt={`${brand.name} logo`} className="h-full w-full object-contain" />}
        </div>
        <div>
          <h1 className="text-3xl font-bold tracking-tight text-slate-900">{brand.name}</h1>
          <Badge variant="secondary" className="mt-3 bg-emerald-50 text-emerald-800 hover:bg-emerald-50">
            {brand.category}
          </Badge>
        </div>
      </div>

      <div className="mt-10">
        <h2 className="text-2xl font-semibold tracking-tight text-slate-900">Productos de {brand.name}</h2>
        {products.length > 0 ? (
          <div className="mt-6 grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
            {products.map(p => <ProductCard key={p.id} product={p} />)}
          </div>
        ) : (
          <div className="mt-6 flex flex-col items-center justify-center rounded-2xl border border-dashed border-slate-300 bg-white/60 px-6 py-16 text-center">
            <PackageOpen className="h-12 w-12 text-slate-400" />
            <h3 className="mt-4 text-lg font-semibold text-slate-800">Esta marca no tiene productos</h3>
            <p className="mt-1 text-sm text-slate-500">Actualmente no hay productos para mostrar.</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default BrandDetail;
